import { RequestsPage, type PrefillSample } from './RequestsPage';
import {
  CheckCircleOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  LeftOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  RightOutlined,
  StopOutlined,
  UpOutlined,
} from '@ant-design/icons';
import {
  Alert,
  AutoComplete,
  Button,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Dropdown,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Result,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Tree,
  Typography,
  message,
} from 'antd';
import type { CurrentUser } from '@cell-bank/shared';
import type { DataNode } from 'antd/es/tree';
import { ProCard } from '@ant-design/pro-components';
import dayjs, { type Dayjs } from 'dayjs';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { BatchPutawayRowValues } from '../lib/batchPutaway';
import { CryovialEntryModal } from '../components/storage/CryovialEntryModal';
import { StorageBoxWorkspace } from '../components/storage/StorageBoxWorkspace';
import { formatTenantDate } from '../lib/tenant';

const ROOT_ID = 'root';

function StorageInfoCard({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="storage-info-card">
      <span className="storage-info-card-label">{label}</span>
      <span className="storage-info-card-value">{children}</span>
    </div>
  );
}

type Spec = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  rows: number;
  columns: number;
  rowLabelRule: string;
  columnLabelRule: string;
  positionOrder: string;
  isDefault: boolean;
};
type DeviceSpec = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  deviceType: string;
  canisterCount: number;
  boxCount: number;
};
type LocationType = 'ROOT' | 'SITE' | 'DEVICE' | 'SHELF' | 'BOX';
type Location = {
  id: string;
  parentId: string | null;
  type: LocationType;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  path: string;
  status: string;
  version: number;
  managerUserId: string | null;
  managerUsername: string | null;
  description: string | null;
  deviceSpecId: string | null;
  deviceSpecCode: string | null;
  deviceSpecNameZh: string | null;
  deviceSpecNameEn: string | null;
  deviceSpecDeviceType: string | null;
  capacityLocked: boolean;
  hasSamplesInSubtree: boolean;
  capacityTotal: number;
  capacityOccupied: number;
  canisterCount: number | null;
  canisterNamingRule: string | null;
  defaultBoxSpecId: string | null;
  defaultBoxSpecCode: string | null;
  defaultBoxSpecNameZh: string | null;
  defaultBoxSpecNameEn: string | null;
  boxCount: number | null;
  boxNamingRule: string | null;
  boxSpecId: string | null;
  rows: number | null;
  columns: number | null;
};
type Manager = {
  id: string;
  username: string;
};
export type Position = {
  id: string;
  label: string;
  rowIndex: number;
  columnIndex: number;
  occupied: boolean;
  sample: {
    id: string;
    sampleNo: string;
    status: string;
    barcode?: string | null;
    cellLineId?: string;
    cellMaterialId?: string | null;
    cellLineCode: string;
    materialName?: string | null;
    materialType?: MaterialType | null;
    passage?: string | null;
    cellCount?: string | null;
    freezeDate?: string | null;
    expiryDate?: string | null;
    storagePositionId?: string | null;
    customFields?: Record<string, unknown> | null;
  } | null;
};
export type Box = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  rows: number;
  columns: number;
  positionOrder: string;
  positions: Position[];
};
type CellLine = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
};
type MaterialType =
  | 'PARENTAL'
  | 'TRANSIENT_POOL'
  | 'STABLE_POOL'
  | 'CLONE'
  | 'ENGINEERED_LINE'
  | 'MIXED_POPULATION'
  | 'OTHER';
type ModificationType =
  | 'KI'
  | 'TAG'
  | 'KO'
  | 'OE'
  | 'KD'
  | 'REPORTER'
  | 'OTHER';
type TagPosition = 'N_TERMINAL' | 'C_TERMINAL' | 'INTERNAL' | 'UNSPECIFIED';
type ModificationStrategy =
  | 'ENDOGENOUS_KI'
  | 'EXOGENOUS_OE'
  | 'STABLE_INTEGRATION'
  | 'TRANSIENT_EXPRESSION'
  | 'OTHER';
type ValidationStatus = 'UNVERIFIED' | 'SCREENING' | 'VERIFIED';
type CellMaterial = {
  id: string;
  cellLineId: string;
  cellLineCode: string;
  parentMaterialId: string | null;
  type: MaterialType;
  name: string;
  modificationType: ModificationType | null;
  targetGene: string | null;
  tagName: string | null;
  tagPosition: TagPosition | null;
  modificationStrategy: ModificationStrategy | null;
  constructName: string | null;
  selectionMarker: string | null;
  cloneNo: string | null;
  validationStatus: ValidationStatus;
  notes: string | null;
};
type SampleDetail = {
  id: string;
  sampleNo: string;
  barcode: string | null;
  cellLineId: string;
  cellMaterialId?: string | null;
  materialName?: string | null;
  materialType?: MaterialType | null;
  passage?: string | null;
  status: string;
  cellCount: string | null;
  storagePositionId: string | null;
  freezeDate?: string | null;
  expiryDate?: string | null;
  customFields?: Record<string, unknown> | null;
  version: number;
};
type Kind = 'location' | 'box';
type ModalKind = Kind | 'site' | 'tank' | 'shelf';
type StorageFormValues = Record<
  string,
  string | number | boolean | null | undefined
>;
type SampleFormValues = {
  sampleNo?: string;
  barcode?: string;
  cellLineId: string;
  cellMaterialId?: string;
  cellCount?: string;
  passage?: string;
  label?: string;
  ownerName?: string;
  notes?: string;
  storagePositionId: string;
  freezeDate?: Dayjs;
  expiryDate?: Dayjs;
};
type RemoveSampleFormValues = { reason: string };
type OriginType = 'COMMERCIAL' | 'IN_HOUSE' | 'DERIVED' | 'OTHER';
type CellLineFormValues = {
  code: string;
  species: string;
  originType: OriginType;
  source?: string;
  catalogNo?: string;
  description?: string;
};
type CellMaterialFormValues = {
  cellLineId: string;
  parentMaterialId?: string;
  type: MaterialType;
  name: string;
  modificationType?: ModificationType;
  targetGene?: string;
  tagName?: string;
  tagPosition?: TagPosition;
  modificationStrategy?: ModificationStrategy;
  constructName?: string;
  selectionMarker?: string;
  cloneNo?: string;
  validationStatus: ValidationStatus;
  notes?: string;
};
type BatchPutawayFormValues = {
  reason?: string;
  rows?: BatchPutawayRowValues<Dayjs>[];
};
type BatchPutawayResult =
  | {
      kind: 'request';
      request: {
        id: string;
        requestNo: string;
        operationType: 'PUTAWAY';
        status: string;
        version: number;
      };
      itemCount: number;
    }
  | {
      kind: 'execution';
      transactionId: string;
      transactionNo: string;
      operationType: 'PUTAWAY';
      itemCount: number;
    };

// Archived prototype: kept for a future device-detail iteration, but intentionally
// not mounted in the current storage page.
type PrototypeBox = {
  id: string;
  rack: string;
  box: string;
};

const prototypeRackNames = ['A', 'B', 'C', 'D', 'E', 'F'];
const prototypeBoxes: PrototypeBox[] = prototypeRackNames.flatMap((rack) =>
  Array.from({ length: 11 }, (_, index) => ({
    id: `prototype-box-${rack}-${index + 1}`,
    rack,
    box: String(index + 1).padStart(2, '0'),
  })),
);

export function TankInteriorPrototype({
  device,
  deviceTitle,
}: {
  device: Location;
  deviceTitle: string;
}) {
  const { t } = useTranslation();
  const canvasMountRef = useRef<HTMLDivElement | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const [selectedBoxId, setSelectedBoxId] = useState(
    prototypeBoxes[0]?.id ?? '',
  );
  const selectedBoxIdRef = useRef(selectedBoxId);
  selectedBoxIdRef.current = selectedBoxId;
  const selectedBox = prototypeBoxes.find((box) => box.id === selectedBoxId);

  useEffect(() => {
    const mount = canvasMountRef.current;
    if (!mount) return undefined;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#eef5f8');

    const camera = new THREE.PerspectiveCamera(32, 1, 0.1, 100);
    camera.position.set(6.8, 3.4, 8.8);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.05;
    renderer.domElement.className = 'storage-3d-canvas-element';
    renderer.domElement.setAttribute(
      'aria-label',
      t('storage.prototypeStageLabel'),
    );
    renderer.domElement.style.touchAction = 'none';
    mount.appendChild(renderer.domElement);

    const ambient = new THREE.HemisphereLight('#f8fdff', '#7890a0', 2.4);
    scene.add(ambient);
    const keyLight = new THREE.DirectionalLight('#ffffff', 3.5);
    keyLight.position.set(5, 9, 7);
    scene.add(keyLight);
    const fillLight = new THREE.DirectionalLight('#b7dfff', 1.6);
    fillLight.position.set(-6, 3, 5);
    scene.add(fillLight);

    const tank = new THREE.Group();
    scene.add(tank);

    const shellMaterial = new THREE.MeshPhysicalMaterial({
      color: '#9ebdca',
      roughness: 0.26,
      metalness: 0.04,
      transparent: true,
      opacity: 0.46,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    const shell = new THREE.Mesh(
      new THREE.CylinderGeometry(
        2.42,
        2.25,
        4.65,
        64,
        1,
        false,
        Math.PI,
        Math.PI,
      ),
      shellMaterial,
    );
    shell.position.y = 0;
    tank.add(shell);

    const shoulder = new THREE.Mesh(
      new THREE.SphereGeometry(2.38, 64, 32, 0, Math.PI * 2, 0, Math.PI / 2),
      new THREE.MeshPhysicalMaterial({
        color: '#dfeef3',
        roughness: 0.34,
        transparent: true,
        opacity: 0.58,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    shoulder.scale.y = 0.27;
    shoulder.position.y = 2.27;
    tank.add(shoulder);

    const base = new THREE.Mesh(
      new THREE.CylinderGeometry(2.3, 2.42, 0.34, 64),
      new THREE.MeshStandardMaterial({ color: '#aebfc9', roughness: 0.56 }),
    );
    base.position.y = -2.45;
    tank.add(base);

    const collar = new THREE.Mesh(
      new THREE.CylinderGeometry(1.02, 1.02, 0.34, 64),
      new THREE.MeshStandardMaterial({ color: '#b8c7d0', roughness: 0.5 }),
    );
    collar.position.y = 2.48;
    tank.add(collar);

    const cap = new THREE.Mesh(
      new THREE.CylinderGeometry(1.18, 1.18, 0.42, 64),
      new THREE.MeshStandardMaterial({
        color: '#326da9',
        roughness: 0.3,
        metalness: 0.08,
      }),
    );
    cap.position.y = 2.82;
    tank.add(cap);

    const handleMaterial = new THREE.MeshStandardMaterial({
      color: '#c1cdd4',
      roughness: 0.46,
    });
    [-1, 1].forEach((side) => {
      const handle = new THREE.Mesh(
        new THREE.TorusGeometry(0.38, 0.075, 12, 28, Math.PI),
        handleMaterial,
      );
      handle.position.set(side * 1.72, 2.08, 0.05);
      handle.rotation.z = side * 0.48;
      handle.rotation.x = Math.PI / 2;
      tank.add(handle);
    });

    const ribMaterial = new THREE.MeshStandardMaterial({
      color: '#c6d5dc',
      roughness: 0.44,
      transparent: true,
      opacity: 0.82,
    });
    [-1.42, -0.72, -0.02].forEach((y) => {
      const rib = new THREE.Mesh(
        new THREE.TorusGeometry(2.38, 0.095, 10, 64),
        ribMaterial,
      );
      rib.position.y = y;
      tank.add(rib);
    });

    const interior = new THREE.Group();
    interior.position.y = -0.25;
    tank.add(interior);

    const rackMaterial = new THREE.MeshStandardMaterial({
      color: '#526b7b',
      roughness: 0.48,
      metalness: 0.28,
    });
    const shelfMaterial = new THREE.MeshStandardMaterial({
      color: '#8fa5b1',
      roughness: 0.42,
      metalness: 0.2,
    });
    const boxMaterial = new THREE.MeshStandardMaterial({
      color: '#b9cbd4',
      roughness: 0.55,
      metalness: 0.04,
    });
    const interactiveObjects: THREE.Object3D[] = [];
    const boxMeshes = new Map<string, { body: THREE.Mesh; rim: THREE.Mesh }>();

    const makeBox = (rack: string, box: string, x: number, y: number) => {
      const group = new THREE.Group();
      group.position.set(x, y, 0);
      const boxId = `prototype-box-${rack}-${box}`;
      const body = new THREE.Mesh(
        new THREE.BoxGeometry(0.94, 0.28, 1.05),
        boxMaterial.clone(),
      );
      body.userData.boxId = boxId;
      group.add(body);
      const rim = new THREE.Mesh(
        new THREE.BoxGeometry(0.98, 0.055, 1.08),
        shelfMaterial.clone(),
      );
      rim.position.y = 0.17;
      rim.userData.boxId = boxId;
      group.add(rim);
      boxMeshes.set(boxId, { body, rim });
      interactiveObjects.push(body, rim);
      return group;
    };

    prototypeRackNames.forEach((rack, rackIndex) => {
      const angle =
        Math.PI / 2 + (rackIndex / prototypeRackNames.length) * Math.PI * 2;
      const ringRadius = 1.52;
      const rackGroup = new THREE.Group();
      rackGroup.position.set(
        Math.cos(angle) * ringRadius,
        0,
        Math.sin(angle) * ringRadius,
      );
      rackGroup.rotation.y = Math.PI / 2 - angle;
      [-0.48, 0.48].forEach((x) => {
        const post = new THREE.Mesh(
          new THREE.BoxGeometry(0.08, 3.72, 0.08),
          rackMaterial,
        );
        post.position.set(x, -0.02, 0);
        rackGroup.add(post);
      });
      [-1.86, 1.82].forEach((y) => {
        const rail = new THREE.Mesh(
          new THREE.BoxGeometry(1.05, 0.1, 0.1),
          shelfMaterial,
        );
        rail.position.set(0, y, 0);
        rackGroup.add(rail);
      });
      Array.from({ length: 11 }, (_, boxIndex) => {
        const box = String(boxIndex + 1).padStart(2, '0');
        rackGroup.add(makeBox(rack, box, 0, -1.62 + boxIndex * 0.33));
      });
      interior.add(rackGroup);
    });

    const setBoxSelected = (boxId: string) => {
      boxMeshes.forEach(({ body, rim }) => {
        (body.material as THREE.MeshStandardMaterial).color.set('#b9cbd4');
        (rim.material as THREE.MeshStandardMaterial).color.set('#8fa5b1');
      });
      const selected = boxMeshes.get(boxId);
      if (!selected) return;
      (selected.body.material as THREE.MeshStandardMaterial).color.set(
        '#4e9fd0',
      );
      (selected.rim.material as THREE.MeshStandardMaterial).color.set(
        '#9ed8ef',
      );
    };
    setBoxSelected(selectedBoxIdRef.current);

    const liquid = new THREE.Mesh(
      new THREE.CylinderGeometry(2.05, 2.05, 0.18, 64),
      new THREE.MeshPhysicalMaterial({
        color: '#8dd5eb',
        roughness: 0.14,
        transmission: 0.22,
        transparent: true,
        opacity: 0.42,
      }),
    );
    liquid.position.y = -2.08;
    tank.add(liquid);

    const windowFrameMaterial = new THREE.MeshStandardMaterial({
      color: '#8eaebd',
      roughness: 0.36,
      transparent: true,
      opacity: 0.8,
    });
    const addWindowBeam = (
      width: number,
      height: number,
      x: number,
      y: number,
    ) => {
      const beam = new THREE.Mesh(
        new THREE.BoxGeometry(width, height, 0.08),
        windowFrameMaterial,
      );
      beam.position.set(x, y, 2.25);
      tank.add(beam);
    };
    addWindowBeam(3.85, 0.045, 0, 2.02);
    addWindowBeam(3.85, 0.045, 0, -2.12);
    addWindowBeam(0.045, 4.14, -1.92, -0.05);
    addWindowBeam(0.045, 4.14, 1.92, -0.05);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.enablePan = false;
    controls.minDistance = 6.2;
    controls.maxDistance = 13;
    controls.minPolarAngle = 0.76;
    controls.maxPolarAngle = 1.95;
    controls.minAzimuthAngle = -1.16;
    controls.maxAzimuthAngle = 1.16;
    controls.target.set(0, 0, 0);
    controlsRef.current = controls;

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    const updatePointer = (event: PointerEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    };
    const findBoxAtPointer = (event: PointerEvent) => {
      updatePointer(event);
      raycaster.setFromCamera(pointer, camera);
      const hit = raycaster.intersectObjects(interactiveObjects, false)[0];
      return hit?.object.userData.boxId as string | undefined;
    };
    const onPointerMove = (event: PointerEvent) => {
      renderer.domElement.style.cursor = findBoxAtPointer(event)
        ? 'pointer'
        : 'grab';
    };
    const onPointerLeave = () => {
      renderer.domElement.style.cursor = 'grab';
    };
    const onClick = (event: MouseEvent) => {
      const boxId = findBoxAtPointer(event as unknown as PointerEvent);
      if (boxId) {
        setBoxSelected(boxId);
        selectedBoxIdRef.current = boxId;
        setSelectedBoxId(boxId);
      }
    };
    renderer.domElement.addEventListener('pointermove', onPointerMove);
    renderer.domElement.addEventListener('pointerleave', onPointerLeave);
    renderer.domElement.addEventListener('click', onClick);

    const resize = () => {
      const width = Math.max(mount.clientWidth, 320);
      const height = Math.max(mount.clientHeight, 420);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height, false);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(mount);
    resize();

    let frame = 0;
    const animate = () => {
      frame = window.requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      renderer.domElement.removeEventListener('pointermove', onPointerMove);
      renderer.domElement.removeEventListener('pointerleave', onPointerLeave);
      renderer.domElement.removeEventListener('click', onClick);
      controls.dispose();
      controlsRef.current = null;
      cameraRef.current = null;
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh) {
          const mesh = object as THREE.Mesh<
            THREE.BufferGeometry,
            THREE.Material | THREE.Material[]
          >;
          mesh.geometry.dispose();
          const materials = Array.isArray(mesh.material)
            ? mesh.material
            : [mesh.material];
          materials.forEach((material) => material.dispose());
        }
      });
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, [t]);

  const rotate = (delta: number) => {
    const controls = controlsRef.current;
    if (!controls) return;
    const offset = controls.object.position.clone().sub(controls.target);
    offset.applyAxisAngle(new THREE.Vector3(0, 1, 0), delta);
    controls.object.position.copy(controls.target).add(offset);
    controls.update();
  };

  const resetView = () => {
    const controls = controlsRef.current;
    const camera = cameraRef.current;
    if (!controls || !camera) return;
    camera.position.set(6.8, 3.4, 8.8);
    controls.target.set(0, 0, 0);
    controls.update();
  };

  return (
    <section
      className="storage-3d-prototype"
      aria-labelledby="storage-3d-title"
    >
      <div className="storage-3d-prototype-header">
        <div>
          <Space size={8} wrap>
            <Typography.Title level={5} id="storage-3d-title">
              {t('storage.interiorPrototype')}
            </Typography.Title>
            <Tag color="blue">{t('storage.prototypeTag')}</Tag>
          </Space>
          <Typography.Text type="secondary">
            {deviceTitle || device.code} · {t('storage.prototypeHint')}
          </Typography.Text>
        </div>
        <Space size={4}>
          <Button
            size="small"
            icon={<LeftOutlined />}
            aria-label={t('storage.prototypeRotateLeft')}
            title={t('storage.prototypeRotateLeft')}
            onClick={() => rotate(-10)}
          />
          <Button
            size="small"
            icon={<ReloadOutlined />}
            aria-label={t('storage.prototypeResetView')}
            title={t('storage.prototypeResetView')}
            onClick={resetView}
          />
          <Button
            size="small"
            icon={<RightOutlined />}
            aria-label={t('storage.prototypeRotateRight')}
            title={t('storage.prototypeRotateRight')}
            onClick={() => rotate(10)}
          />
        </Space>
      </div>

      <div className="storage-3d-content">
        <div
          className="storage-3d-stage"
          aria-label={t('storage.prototypeStageLabel')}
        >
          <div className="storage-3d-stage-caption">
            <span>{t('storage.prototypeCutaway')}</span>
            <span>{t('storage.prototypeInteractionHint')}</span>
          </div>
          <div
            ref={canvasMountRef}
            className="storage-3d-canvas"
            role="application"
            aria-label={t('storage.prototypeStageLabel')}
          />
        </div>

        <aside className="storage-3d-prototype-details">
          <Typography.Text className="storage-3d-details-kicker">
            {t('storage.prototypeDetails')}
          </Typography.Text>
          {selectedBox ? (
            <>
              <Typography.Title level={5}>
                {selectedBox.rack} {t('storage.prototypeRack')} ·{' '}
                {selectedBox.box} {t('storage.prototypeBox')}
              </Typography.Title>
              <Descriptions size="small" column={1}>
                <Descriptions.Item label={t('storage.prototypePosition')}>
                  {selectedBox.rack} {t('storage.prototypeRack')} ·{' '}
                  {selectedBox.box} {t('storage.prototypeBox')}
                </Descriptions.Item>
                <Descriptions.Item label={t('storage.prototypeCount')}>
                  {prototypeRackNames.length} ×{' '}
                  {prototypeBoxes.length / prototypeRackNames.length}
                </Descriptions.Item>
              </Descriptions>
            </>
          ) : (
            <Typography.Text type="secondary">
              {t('storage.prototypeSelectBox')}
            </Typography.Text>
          )}
          <Divider />
          <div className="storage-3d-legend">
            <span>
              <i className="storage-3d-legend-swatch is-occupied" />
              {t('storage.prototypeSelectedBox')}
            </span>
            <span>
              <i className="storage-3d-legend-swatch is-empty" />
              {t('storage.prototypeBoxLegend')}
            </span>
          </div>
          <Typography.Paragraph type="secondary" className="storage-3d-note">
            {t('storage.prototypeNote')}
          </Typography.Paragraph>
        </aside>
      </div>
    </section>
  );
}

export function StoragePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<CurrentUser>();
  const [specs, setSpecs] = useState<Spec[]>([]);
  const [deviceSpecs, setDeviceSpecs] = useState<DeviceSpec[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [managers, setManagers] = useState<Manager[]>([]);
  const [cellLines, setCellLines] = useState<CellLine[]>([]);
  const [materials, setMaterials] = useState<CellMaterial[]>([]);
  const [selectedId, setSelectedId] = useState<string>();
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [box, setBox] = useState<Box>();
  const boxLoadVersion = useRef(0);
  const [boxLoading, setBoxLoading] = useState(false);
  const [boxLoadFailed, setBoxLoadFailed] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<Position>();
  const [selectedPositionIds, setSelectedPositionIds] = useState<string[]>([]);
  const [boxLabelMode, setBoxLabelMode] = useState<'alphanumeric' | 'numeric'>(
    () =>
      window.localStorage.getItem('storage.boxLabelMode') === 'numeric'
        ? 'numeric'
        : 'alphanumeric',
  );
  const [positionDisplay, setPositionDisplay] = useState<
    'none' | 'sampleNo' | 'cellLineMaterial' | 'label' | 'passage'
  >(() => {
    const saved = window.localStorage.getItem('storage.positionDisplay');
    return saved === 'none' ||
      saved === 'sampleNo' ||
      saved === 'label' ||
      saved === 'passage'
      ? saved
      : 'cellLineMaterial';
  });
  const [boxViewMode, setBoxViewMode] = useState<'grid' | 'list'>(() =>
    window.localStorage.getItem('storage.boxViewMode') === 'list'
      ? 'list'
      : 'grid',
  );
  const [boxActionsOpen, setBoxActionsOpen] = useState(false);
  const [locationActionsOpen, setLocationActionsOpen] = useState(false);
  const [requestSamples, setRequestSamples] = useState<PrefillSample[]>();
  const [sampleOpen, setSampleOpen] = useState(false);
  const [editingSample, setEditingSample] = useState<SampleDetail>();
  const [cellLineOpen, setCellLineOpen] = useState(false);
  const [materialOpen, setMaterialOpen] = useState(false);
  const [removingSample, setRemovingSample] = useState<SampleDetail>();
  const [batchPutawayOpen, setBatchPutawayOpen] = useState(false);
  const [entryPositionIds, setEntryPositionIds] = useState<string[]>([]);
  const [entryError, setEntryError] = useState('');
  const [batchPutawaySubmitting, setBatchPutawaySubmitting] = useState(false);
  const [batchPutawayResult, setBatchPutawayResult] =
    useState<BatchPutawayResult>();
  const [batchRemovingSamples, setBatchRemovingSamples] = useState<
    SampleDetail[]
  >([]);
  const [cellLinesLoading, setCellLinesLoading] = useState(false);
  const [kind, setKind] = useState<ModalKind>();
  const [form] = Form.useForm<StorageFormValues>();
  const [sampleForm] = Form.useForm<SampleFormValues>();
  const [cellLineForm] = Form.useForm<CellLineFormValues>();
  const [materialForm] = Form.useForm<CellMaterialFormValues>();
  const [removeSampleForm] = Form.useForm<RemoveSampleFormValues>();
  const [batchRemoveForm] = Form.useForm<RemoveSampleFormValues>();
  const [messageApi, holder] = message.useMessage();
  const watchedCanisterCount = Form.useWatch('canisterCount', form);
  const watchedBoxCount = Form.useWatch('boxCount', form);
  const watchedBoxParentId = Form.useWatch('parentId', form);
  const watchedSpecMode = Form.useWatch('specMode', form);
  const watchedLocationType = Form.useWatch('type', form);
  const selectedSampleCellLineId = Form.useWatch('cellLineId', sampleForm) as
    | string
    | undefined;
  const selectedMaterialModificationType = Form.useWatch(
    'modificationType',
    materialForm,
  );
  const canExecuteInventory =
    currentUser?.permissions.includes('inventory:execute') ?? false;

  const title = (item: { nameZh: string | null; nameEn: string | null }) =>
    i18n.resolvedLanguage === 'en-US'
      ? (item.nameEn ?? item.nameZh ?? '')
      : (item.nameZh ?? item.nameEn ?? '');

  const typeLabel = (type: string) => t(`storage.locationType.${type}`);
  const locationLabel = (item: Location) =>
    `${title(item) || item.code} · ${typeLabel(item.type)}`;
  const selectedLocationLabel = (item: Location) =>
    item.type === 'SHELF' ? title(item) || item.code : locationLabel(item);
  const specLabel = (item?: Spec | null) =>
    item ? `${title(item) || item.code} (${item.rows} × ${item.columns})` : '-';
  const deviceSpecLabel = (item?: DeviceSpec | null) =>
    item
      ? `${title(item) || item.code} · ${item.canisterCount} × ${item.boxCount}`
      : '-';
  const boxSpecLabel = (item: Location) => {
    if (item.type !== 'BOX') return '-';
    const spec = item.boxSpecId
      ? specs.find((candidate) => candidate.id === item.boxSpecId)
      : null;
    if (spec) return specLabel(spec);
    return item.rows && item.columns ? `${item.rows} × ${item.columns}` : '-';
  };
  const managerLabel = (item?: { managerUsername: string | null }) => {
    if (!item?.managerUsername) return '-';
    return item.managerUsername;
  };
  const managerOptions = managers.map((item) => {
    return {
      value: item.id,
      label: item.username,
    };
  });
  const cellLineOptions = cellLines.map((line) => {
    const display =
      i18n.resolvedLanguage === 'en-US'
        ? (line.nameEn ?? line.nameZh)
        : (line.nameZh ?? line.nameEn);
    return {
      value: line.id,
      code: line.code,
      label:
        display && display !== line.code
          ? `${line.code} — ${display}`
          : line.code,
    };
  });
  const originTypeOptions = (
    ['COMMERCIAL', 'IN_HOUSE', 'DERIVED', 'OTHER'] as OriginType[]
  ).map((value) => ({
    value,
    label: t(`cellLines.originTypeOptions.${value}`),
  }));
  const speciesOptions = [
    'Homo sapiens',
    'Mus musculus',
    'Rattus norvegicus',
    'Cricetulus griseus',
    'Chlorocebus sabaeus',
    'Mesocricetus auratus',
  ].map((value) => ({ value }));
  const materialTypeOptions = (
    [
      'PARENTAL',
      'TRANSIENT_POOL',
      'STABLE_POOL',
      'CLONE',
      'ENGINEERED_LINE',
      'MIXED_POPULATION',
      'OTHER',
    ] as MaterialType[]
  ).map((value) => ({
    value,
    label: t(`samples.materialTypeOptions.${value}`),
  }));
  const modificationTypeOptions = (
    ['KI', 'TAG', 'KO', 'OE', 'KD', 'REPORTER', 'OTHER'] as ModificationType[]
  ).map((value) => ({
    value,
    label: t(`samples.modificationTypeOptions.${value}`),
  }));
  const tagPositionOptions = (
    ['N_TERMINAL', 'C_TERMINAL', 'INTERNAL', 'UNSPECIFIED'] as TagPosition[]
  ).map((value) => ({
    value,
    label: t(`samples.tagPositionOptions.${value}`),
  }));
  const modificationStrategyOptions = (
    [
      'ENDOGENOUS_KI',
      'EXOGENOUS_OE',
      'STABLE_INTEGRATION',
      'TRANSIENT_EXPRESSION',
      'OTHER',
    ] as ModificationStrategy[]
  ).map((value) => ({
    value,
    label: t(`samples.modificationStrategyOptions.${value}`),
  }));
  const validationStatusOptions = (
    ['UNVERIFIED', 'SCREENING', 'VERIFIED'] as ValidationStatus[]
  ).map((value) => ({
    value,
    label: t(`samples.validationStatusOptions.${value}`),
  }));
  const materialLabel = (material?: CellMaterial | null) =>
    material
      ? `${material.name} · ${t(`samples.materialTypeOptions.${material.type}`)}`
      : '-';
  const helpLabel = (label: string, help: string) => (
    <Space size={4} align="center">
      <span>{label}</span>
      <Tooltip title={help}>
        <QuestionCircleOutlined
          aria-label={help}
          style={{ color: '#8c8c8c' }}
        />
      </Tooltip>
    </Space>
  );
  const materialsForLine = (cellLineId?: string) =>
    materials.filter((material) => material.cellLineId === cellLineId);
  const defaultMaterialId = (cellLineId?: string) =>
    materialsForLine(cellLineId).find(
      (material) => material.type === 'PARENTAL',
    )?.id;
  const formatDate = (value?: Dayjs) => value?.format('YYYY-MM-DD');
  const dateValue = (value?: string | null) => {
    const date = formatTenantDate(value);
    return date === '-' ? undefined : dayjs(date);
  };
  const ownerName = (sample?: Pick<SampleDetail, 'customFields'>) => {
    const value = sample?.customFields?.ownerName;
    return typeof value === 'string' ? value : undefined;
  };
  const sampleLabel = (sample?: Pick<SampleDetail, 'customFields'>) => {
    const value = sample?.customFields?.label;
    return typeof value === 'string' ? value : undefined;
  };
  const sampleNotes = (sample?: Pick<SampleDetail, 'customFields'>) => {
    const value = sample?.customFields?.notes;
    return typeof value === 'string' ? value : undefined;
  };
  const saveCellLine = async (values: CellLineFormValues) => {
    const response = await fetch('/api/v1/cell-lines', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(values),
    });
    if (!response.ok) {
      await messageApi.error(
        response.status === 409
          ? t('cellLines.duplicate')
          : t('cellLines.saveFailed'),
      );
      return;
    }
    const created = (await response.json()) as CellLine;
    setCellLines((current) => [created, ...current]);
    sampleForm.setFieldValue('cellLineId', created.id);
    setCellLineOpen(false);
    cellLineForm.resetFields();
    await messageApi.success(t('cellLines.saved'));
  };
  const openCreateMaterial = (lineId?: string) => {
    const cellLineId =
      lineId ?? (sampleForm.getFieldValue('cellLineId') as string | undefined);
    if (!cellLineId) {
      void messageApi.error(t('samples.selectCellLineFirst'));
      return;
    }
    materialForm.resetFields();
    materialForm.setFieldsValue({
      cellLineId,
      type: 'TRANSIENT_POOL',
      validationStatus: 'UNVERIFIED',
    });
    setMaterialOpen(true);
  };
  const saveMaterial = async (values: CellMaterialFormValues) => {
    const response = await fetch('/api/v1/cell-materials', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(values),
    });
    if (!response.ok) {
      await messageApi.error(t('samples.materialSaveFailed'));
      return;
    }
    const created = (await response.json()) as CellMaterial;
    setMaterials((current) => [created, ...current]);
    sampleForm.setFieldValue('cellMaterialId', created.id);
    setMaterialOpen(false);
    materialForm.resetFields();
    await messageApi.success(t('samples.materialSaved'));
  };

  const locationById = useMemo(
    () => new Map(locations.map((item) => [item.id, item])),
    [locations],
  );
  const locationLabelByPath = useMemo(
    () =>
      new Map(locations.map((item) => [item.path, title(item) || item.code])),
    [locations, i18n.resolvedLanguage],
  );
  const displayPath = (path?: string | null) => {
    if (!path) return '-';
    const parts = path.split('/').filter(Boolean);
    return parts
      .map((_, index) => {
        const segmentPath = `/${parts.slice(0, index + 1).join('/')}`;
        return locationLabelByPath.get(segmentPath) ?? parts[index];
      })
      .join('/');
  };
  const rootLocation: Location = {
    id: ROOT_ID,
    parentId: null,
    type: 'ROOT',
    code: 'ROOT',
    nameZh: t('storage.rootName'),
    nameEn: 'System',
    path: '/ROOT',
    status: 'ACTIVE',
    version: 0,
    managerUserId: null,
    managerUsername: null,
    description: null,
    deviceSpecId: null,
    deviceSpecCode: null,
    deviceSpecNameZh: null,
    deviceSpecNameEn: null,
    deviceSpecDeviceType: null,
    capacityLocked: true,
    hasSamplesInSubtree: true,
    capacityTotal: 0,
    capacityOccupied: 0,
    canisterCount: null,
    canisterNamingRule: null,
    defaultBoxSpecId: null,
    defaultBoxSpecCode: null,
    defaultBoxSpecNameZh: null,
    defaultBoxSpecNameEn: null,
    boxCount: null,
    boxNamingRule: null,
    boxSpecId: null,
    rows: null,
    columns: null,
  };
  const selected = selectedId
    ? selectedId === ROOT_ID
      ? rootLocation
      : locationById.get(selectedId)
    : undefined;
  const children = useMemo(
    () =>
      locations.filter((item) =>
        selected?.id === ROOT_ID
          ? item.parentId === null
          : item.parentId === (selected?.id ?? null),
      ),
    [locations, selected],
  );
  const selectedCanisters = useMemo(
    () =>
      selected?.type === 'DEVICE'
        ? locations.filter(
            (item) => item.parentId === selected.id && item.type === 'SHELF',
          )
        : [],
    [locations, selected],
  );
  const selectedBoxes = useMemo(
    () =>
      selected?.type === 'SHELF'
        ? locations.filter(
            (item) => item.parentId === selected.id && item.type === 'BOX',
          )
        : [],
    [locations, selected],
  );
  const boxSiblings = useMemo(
    () =>
      selected?.type === 'BOX'
        ? locations
            .filter(
              (item) =>
                item.parentId === selected.parentId && item.type === 'BOX',
            )
            .sort((a, b) =>
              (a.code || '').localeCompare(b.code || '', undefined, {
                numeric: true,
                sensitivity: 'base',
              }),
            )
        : [],
    [locations, selected],
  );
  const boxSiblingIndex =
    selected?.type === 'BOX'
      ? boxSiblings.findIndex((item) => item.id === selected.id)
      : -1;
  const previousBox =
    boxSiblingIndex > 0 ? boxSiblings[boxSiblingIndex - 1] : undefined;
  const nextBox =
    boxSiblingIndex >= 0 && boxSiblingIndex < boxSiblings.length - 1
      ? boxSiblings[boxSiblingIndex + 1]
      : undefined;
  const selectedCanisterCount = selectedCanisters.length;
  const selectedBoxCount = selectedBoxes.length;
  const maxTankCanisterBoxCount = useMemo(
    () =>
      selected?.type === 'DEVICE'
        ? Math.max(
            0,
            ...selectedCanisters.map(
              (canister) =>
                locations.filter(
                  (item) =>
                    item.parentId === canister.id && item.type === 'BOX',
                ).length,
            ),
          )
        : 0,
    [locations, selected, selectedCanisters],
  );
  const nextCanisterCount =
    typeof watchedCanisterCount === 'number' ? watchedCanisterCount : null;
  const nextBoxCount =
    typeof watchedBoxCount === 'number' ? watchedBoxCount : null;
  const isReducingCanisters =
    kind === 'tank' &&
    nextCanisterCount !== null &&
    nextCanisterCount < selectedCanisterCount;
  const isReducingBoxes =
    kind === 'shelf' &&
    nextBoxCount !== null &&
    nextBoxCount < selectedBoxCount;
  const isReducingTankBoxes =
    kind === 'tank' &&
    nextBoxCount !== null &&
    nextBoxCount < maxTankCanisterBoxCount;
  const occupiedCount =
    box?.positions.filter((item) => item.occupied).length ?? 0;
  const selectedPositions = useMemo(
    () =>
      box?.positions.filter((position) =>
        selectedPositionIds.includes(position.id),
      ) ?? [],
    [box, selectedPositionIds],
  );
  const selectedEmptyPositions = selectedPositions.filter(
    (position) => !position.occupied,
  );
  const selectedOccupiedPositions = selectedPositions.filter(
    (position) => position.occupied,
  );
  const globalDefaultSpec = specs.find((item) => item.isDefault) ?? specs[0];
  const inheritedBoxSpecId = (parentId?: string | null) => {
    const parent = parentId ? locationById.get(parentId) : undefined;
    if (!parent) return globalDefaultSpec?.id;
    if (parent.defaultBoxSpecId) return parent.defaultBoxSpecId;
    const grandparent = parent.parentId
      ? locationById.get(parent.parentId)
      : undefined;
    return grandparent?.defaultBoxSpecId ?? globalDefaultSpec?.id;
  };
  const inheritedBoxSpec = specs.find(
    (item) => item.id === inheritedBoxSpecId(String(watchedBoxParentId ?? '')),
  );
  const parentTank =
    selected?.type === 'SHELF' && selected.parentId
      ? locationById.get(selected.parentId)
      : undefined;
  const effectiveShelfBoxSpec =
    selected?.type === 'SHELF'
      ? specs.find((item) => item.id === inheritedBoxSpecId(selected.id))
      : undefined;
  const inheritedShelfBoxRule =
    selected?.type === 'SHELF' ? (parentTank?.boxNamingRule ?? null) : null;
  const inheritedShelfBoxCount =
    selected?.type === 'SHELF' ? (parentTank?.boxCount ?? null) : null;

  const treeData = useMemo<DataNode[]>(() => {
    const build = (parentId: string | null): DataNode[] =>
      locations
        .filter((item) => item.parentId === parentId)
        .map((item) => {
          const nodeChildren = build(item.id);
          const node: DataNode = {
            key: item.id,
            title: (
              <Tooltip
                title={
                  item.status === 'INACTIVE' ? t('storage.inactive') : undefined
                }
              >
                <span
                  className={`storage-tree-title${item.status === 'INACTIVE' ? ' is-inactive' : ''}`}
                >
                  {item.status === 'INACTIVE' && (
                    <span className="storage-tree-status-dot" />
                  )}
                  {title(item) || item.code}
                </span>
              </Tooltip>
            ),
          };
          if (nodeChildren.length) node.children = nodeChildren;
          return node;
        });
    return [
      {
        key: ROOT_ID,
        title: (
          <span className="storage-tree-title">{t('storage.rootName')}</span>
        ),
        children: build(null),
      },
    ];
  }, [locations, i18n.resolvedLanguage, t]);
  const defaultExpandedKeys = useMemo(
    () => [
      ROOT_ID,
      ...locations
        .filter((item) => item.type === 'SITE' || item.type === 'DEVICE')
        .map((item) => item.id),
    ],
    [locations],
  );

  useEffect(() => {
    if (!locations.length) return;
    const validKeys = new Set([ROOT_ID, ...locations.map((item) => item.id)]);
    setExpandedKeys((current) => {
      const next = current.filter((key) => validKeys.has(key));
      return next.length ? next : defaultExpandedKeys;
    });
  }, [locations, defaultExpandedKeys]);

  const load = async () => {
    try {
      const [
        specResponse,
        deviceSpecResponse,
        locationResponse,
        managerResponse,
      ] = await Promise.all([
        fetch('/api/v1/storage-box-specs', { credentials: 'include' }),
        fetch('/api/v1/storage-device-specs', { credentials: 'include' }),
        fetch('/api/v1/storage-locations', { credentials: 'include' }),
        fetch('/api/v1/storage-managers', { credentials: 'include' }),
      ]);
      if (
        !specResponse.ok ||
        !deviceSpecResponse.ok ||
        !locationResponse.ok ||
        !managerResponse.ok
      )
        throw new Error();
      const nextSpecs = (await specResponse.json()) as Spec[];
      const nextDeviceSpecs = (await deviceSpecResponse.json()) as DeviceSpec[];
      const nextLocations = (await locationResponse.json()) as Location[];
      const nextManagers = (await managerResponse.json()) as {
        items: Manager[];
      };
      setSpecs(nextSpecs);
      setDeviceSpecs(nextDeviceSpecs);
      setLocations(nextLocations);
      setManagers(nextManagers.items);
      setSelectedId((current) => current ?? nextLocations[0]?.id);
    } catch {
      await messageApi.error(t('storage.loadFailed'));
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    void fetch('/api/v1/auth/me', { credentials: 'include' })
      .then(async (response) => {
        if (response.ok) setCurrentUser((await response.json()) as CurrentUser);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    setSelectedPosition(undefined);
    setSelectedPositionIds([]);
    setBoxActionsOpen(false);
    setLocationActionsOpen(false);
    boxLoadVersion.current += 1;
    if (selected?.type === 'BOX') {
      void inspect(selected.id);
      return;
    }
    setBox(undefined);
    setBoxLoading(false);
  }, [selected?.id]);

  useEffect(() => {
    if (!box) return;
    const positionIds = new Set(box.positions.map((position) => position.id));
    setSelectedPositionIds((current) =>
      current.filter((id) => positionIds.has(id)),
    );
  }, [box]);

  const inspect = async (id: string) => {
    const loadVersion = ++boxLoadVersion.current;
    setBoxLoading(true);
    setBoxLoadFailed(false);
    try {
      const response = await fetch(`/api/v1/storage-boxes/${id}`, {
        credentials: 'include',
      });
      if (!response.ok) throw new Error();
      const nextBox = (await response.json()) as Box;
      if (loadVersion !== boxLoadVersion.current) return undefined;
      setBox(nextBox);
      return nextBox;
    } catch {
      if (loadVersion === boxLoadVersion.current) setBoxLoadFailed(true);
      return undefined;
    } finally {
      if (loadVersion === boxLoadVersion.current) setBoxLoading(false);
    }
  };

  const loadCellLines = async () => {
    if (cellLines.length && materials.length) return true;
    setCellLinesLoading(true);
    try {
      const [linesResponse, materialsResponse] = await Promise.all([
        fetch('/api/v1/cell-lines?limit=100', { credentials: 'include' }),
        fetch('/api/v1/cell-materials?limit=200', { credentials: 'include' }),
      ]);
      if (!linesResponse.ok || !materialsResponse.ok) throw new Error();
      setCellLines(
        ((await linesResponse.json()) as { items: CellLine[] }).items,
      );
      setMaterials(
        ((await materialsResponse.json()) as { items: CellMaterial[] }).items,
      );
      return true;
    } catch {
      await messageApi.error(t('samples.loadFailed'));
      return false;
    } finally {
      setCellLinesLoading(false);
    }
  };

  useEffect(() => {
    if (!sampleOpen || editingSample || !selectedSampleCellLineId) return;
    if (sampleForm.getFieldValue('cellMaterialId')) return;
    const parentalId = defaultMaterialId(selectedSampleCellLineId);
    if (parentalId) sampleForm.setFieldValue('cellMaterialId', parentalId);
  }, [selectedSampleCellLineId, materials, sampleOpen, editingSample]);

  const refreshSelectedBox = async (positionId?: string) => {
    if (!selected || selected.type !== 'BOX') return;
    const [nextBox] = await Promise.all([inspect(selected.id), load()]);
    if (positionId && nextBox)
      setSelectedPosition(
        nextBox.positions.find((position) => position.id === positionId),
      );
  };

  const clearPositionSelection = () => setSelectedPositionIds([]);

  const openEditSample = async (position?: Position) => {
    if (!position?.sample) return;
    if (!(await loadCellLines())) return;
    const response = await fetch(`/api/v1/samples/${position.sample.id}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      await messageApi.error(t('samples.detailFailed'));
      return;
    }
    const sample = (await response.json()) as SampleDetail;
    setSelectedPosition(position);
    setEditingSample(sample);
    sampleForm.resetFields();
    const values: Partial<SampleFormValues> = {
      sampleNo: sample.sampleNo,
      cellLineId: sample.cellLineId,
      storagePositionId: sample.storagePositionId ?? position.id,
    };
    if (sample.cellMaterialId) values.cellMaterialId = sample.cellMaterialId;
    if (sample.cellCount) values.cellCount = sample.cellCount;
    if (sample.passage) values.passage = sample.passage;
    const label = sampleLabel(sample);
    if (label) values.label = label;
    const nextOwnerName = ownerName(sample);
    if (nextOwnerName) values.ownerName = nextOwnerName;
    const notes = sampleNotes(sample);
    if (notes) values.notes = notes;
    if (sample.barcode) values.barcode = sample.barcode;
    const freezeDate = dateValue(sample.freezeDate);
    const expiryDate = dateValue(sample.expiryDate);
    if (freezeDate) values.freezeDate = freezeDate;
    if (expiryDate) values.expiryDate = expiryDate;
    sampleForm.setFieldsValue(values);
    setSampleOpen(true);
  };

  const saveSample = async (values: SampleFormValues) => {
    const body = {
      sampleNo: editingSample ? values.sampleNo?.trim() : undefined,
      barcode: values.barcode,
      cellLineId: values.cellLineId,
      cellMaterialId: values.cellMaterialId,
      cellCount: values.cellCount,
      passage: values.passage,
      storagePositionId: values.storagePositionId,
      freezeDate: formatDate(values.freezeDate),
      expiryDate: formatDate(values.expiryDate),
      customFields: {
        ...(editingSample?.customFields ?? {}),
        label: values.label?.trim() || undefined,
        ownerName: values.ownerName?.trim() || undefined,
        notes: values.notes?.trim() || undefined,
      },
    };
    const response = await fetch(
      editingSample ? `/api/v1/samples/${editingSample.id}` : '/api/v1/samples',
      {
        method: editingSample ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(
          editingSample ? { ...body, version: editingSample.version } : body,
        ),
      },
    );
    if (!response.ok) {
      await messageApi.error(t('samples.saveFailed'));
      return;
    }
    setSampleOpen(false);
    setEditingSample(undefined);
    sampleForm.resetFields();
    await refreshSelectedBox(values.storagePositionId);
    await messageApi.success(t('samples.saved'));
  };

  const openRemoveSample = async (position?: Position) => {
    if (!position?.sample) return;
    const response = await fetch(`/api/v1/samples/${position.sample.id}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      await messageApi.error(t('samples.detailFailed'));
      return;
    }
    setRemovingSample((await response.json()) as SampleDetail);
    removeSampleForm.resetFields();
  };

  const removeSample = async (values: RemoveSampleFormValues) => {
    if (!removingSample) return;
    const response = await fetch(
      `/api/v1/samples/${removingSample.id}/transition`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          version: removingSample.version,
          status: 'MISSING',
          storagePositionId: null,
          reason: values.reason,
        }),
      },
    );
    if (!response.ok) {
      await messageApi.error(t('samples.actionFailed'));
      return;
    }
    const positionId = removingSample.storagePositionId ?? undefined;
    setRemovingSample(undefined);
    removeSampleForm.resetFields();
    await refreshSelectedBox(positionId);
    await messageApi.success(t('samples.actionSaved'));
  };

  const openBatchPutaway = async (
    positions: Position[] = selectedEmptyPositions,
  ) => {
    if (
      !positions.length ||
      positions.length > 100 ||
      positions.some((position) => position.occupied)
    )
      return;
    if (!(await loadCellLines())) return;
    setEntryPositionIds(positions.map((position) => position.id));
    setEntryError('');
    setBatchPutawayResult(undefined);
    setBatchPutawayOpen(true);
  };

  const saveBatchPutaway = async (values: BatchPutawayFormValues) => {
    const rows = (values.rows ?? []).filter((row) => row.positionId);
    if (!rows.length) return;
    setBatchPutawaySubmitting(true);
    setEntryError('');
    try {
      const response = await fetch('/api/v1/samples/bulk-putaway', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          reason: values.reason?.trim(),
          requestTitle: t('storage.batchPutawayRequestTitle', {
            count: rows.length,
          }),
          items: rows.map((row) => ({
            barcode: row.barcode,
            cellLineId: row.cellLineId,
            cellMaterialId: row.cellMaterialId,
            cellCount: row.cellCount,
            passage: row.passage,
            storagePositionId: row.positionId,
            freezeDate: formatDate(row.freezeDate),
            expiryDate: formatDate(row.expiryDate),
            customFields: {
              label: row.label?.trim() || undefined,
              ownerName: row.ownerName?.trim() || undefined,
              notes: row.notes?.trim() || undefined,
            },
          })),
        }),
      });
      if (!response.ok) {
        setEntryError(
          t(
            response.status === 404
              ? 'storage.entry.endpointUnavailable'
              : response.status === 409
                ? 'storage.entry.conflict'
                : 'storage.batchPutawayFailed',
          ),
        );
        return;
      }
      const result = (await response.json()) as {
        samples: SampleDetail[];
        putawayRequest?: Extract<
          BatchPutawayResult,
          { kind: 'request' }
        >['request'];
        putawayTransaction?: Omit<
          Extract<BatchPutawayResult, { kind: 'execution' }>,
          'kind'
        >;
      };
      clearPositionSelection();
      if (result.putawayRequest) {
        setBatchPutawayResult({
          kind: 'request',
          request: result.putawayRequest,
          itemCount: result.samples.length,
        });
      } else if (result.putawayTransaction) {
        setBatchPutawayResult({
          kind: 'execution',
          ...result.putawayTransaction,
        });
      } else {
        setBatchPutawayOpen(false);
        await messageApi.success(
          t('storage.batchPutawaySaved', { count: rows.length }),
        );
      }
      await refreshSelectedBox().catch(() =>
        messageApi.warning(t('storage.loadFailed')),
      );
    } catch {
      setEntryError(t('storage.entry.networkError'));
    } finally {
      setBatchPutawaySubmitting(false);
    }
  };

  const closeBatchPutaway = () => {
    if (batchPutawaySubmitting) return;
    setBatchPutawayOpen(false);
    setBatchPutawayResult(undefined);
  };

  const loadSelectedSampleDetails = async (
    positions: Position[] = selectedOccupiedPositions,
  ) => {
    const sampleIds = positions
      .map((position) => position.sample?.id)
      .filter((id): id is string => Boolean(id));
    const details: SampleDetail[] = [];
    for (const sampleId of sampleIds) {
      const response = await fetch(`/api/v1/samples/${sampleId}`, {
        credentials: 'include',
      });
      if (!response.ok) {
        await messageApi.error(t('samples.detailFailed'));
        return undefined;
      }
      details.push((await response.json()) as SampleDetail);
    }
    return details;
  };

  const createRequestForSelectedSamples = async (
    positions: Position[] = selectedOccupiedPositions,
  ) => {
    if (!positions.length) return;
    const details = await loadSelectedSampleDetails(positions);
    if (!details) return;
    setRequestSamples(
      details.map((sample) => {
        const source = positions.find(
          (position) => position.sample?.id === sample.id,
        );
        return {
          sampleId: sample.id,
          sampleNo: sample.sampleNo,
          version: sample.version,
          status: sample.status,
          storagePositionId: sample.storagePositionId,
          cellLineCode: source?.sample?.cellLineCode ?? '',
          materialName:
            sample.materialName ?? source?.sample?.materialName ?? '',
          passage: sample.passage ?? source?.sample?.passage ?? '',
          storagePositionLabel: source?.label ?? '',
        };
      }),
    );
  };

  const openBatchRemoveSamples = async () => {
    if (!selectedOccupiedPositions.length) return;
    const details = await loadSelectedSampleDetails();
    if (!details) return;
    setBatchRemovingSamples(details);
    batchRemoveForm.resetFields();
  };

  const removeSelectedSamples = async (values: RemoveSampleFormValues) => {
    if (!batchRemovingSamples.length) return;
    for (const sample of batchRemovingSamples) {
      const response = await fetch(`/api/v1/samples/${sample.id}/transition`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          version: sample.version,
          status: 'MISSING',
          storagePositionId: null,
          reason: values.reason,
        }),
      });
      if (!response.ok) {
        await messageApi.error(t('samples.actionFailed'));
        return;
      }
    }
    const count = batchRemovingSamples.length;
    setBatchRemovingSamples([]);
    batchRemoveForm.resetFields();
    clearPositionSelection();
    await refreshSelectedBox();
    await messageApi.success(t('storage.batchRemoveSaved', { count }));
  };

  const open = (next: Kind) => {
    form.resetFields();
    if (next === 'location') {
      const createType = selected?.id === ROOT_ID ? 'SITE' : 'DEVICE';
      form.setFieldsValue({
        type: createType,
        parentId: createType === 'DEVICE' ? selected?.id : undefined,
      });
    }
    if (next === 'box') {
      const parentId = selected?.type === 'SHELF' ? selected.id : undefined;
      form.setFieldsValue({
        parentId,
        specMode: 'preset',
        specId: inheritedBoxSpecId(parentId),
        rowLabelRule: 'ALPHA',
        columnLabelRule: 'NUMERIC',
        positionOrder: 'ROW_MAJOR',
      });
    }
    setKind(next);
  };

  const editTank = () => {
    if (!selected || selected.type !== 'DEVICE') return;
    form.resetFields();
    form.setFieldsValue({
      nameZh: selected.nameZh,
      nameEn: selected.nameEn,
      managerUserId: selected.managerUserId,
      canisterCount: selected.canisterCount,
      canisterNamingRule: selected.canisterNamingRule,
      defaultBoxSpecId: selected.defaultBoxSpecId,
      boxCount: selected.boxCount,
      boxNamingRule: selected.boxNamingRule,
      description: selected.description,
      version: selected.version,
    });
    setKind('tank');
  };

  const editSite = () => {
    if (!selected || selected.type !== 'SITE') return;
    form.resetFields();
    form.setFieldsValue({
      code: selected.code,
      nameZh: selected.nameZh,
      nameEn: selected.nameEn,
      version: selected.version,
    });
    setKind('site');
  };

  const editShelf = () => {
    if (!selected || selected.type !== 'SHELF') return;
    form.resetFields();
    form.setFieldsValue({
      nameZh: selected.nameZh,
      nameEn: selected.nameEn,
      managerUserId: selected.managerUserId,
      defaultBoxSpecId: selected.defaultBoxSpecId,
      boxCount: selected.boxCount,
      boxNamingRule: selected.boxNamingRule,
      description: selected.description,
      version: selected.version,
    });
    setKind('shelf');
  };

  useEffect(() => {
    if (kind !== 'box' || watchedSpecMode !== 'preset') return;
    form.setFieldValue(
      'specId',
      inheritedBoxSpecId(String(watchedBoxParentId ?? '')),
    );
  }, [kind, watchedBoxParentId, watchedSpecMode, specs, locations]);

  const save = async (values: StorageFormValues) => {
    if (!kind) return;
    const isSite = kind === 'site';
    const isTank = kind === 'tank';
    const isShelf = kind === 'shelf';
    const path = isSite
      ? `storage-locations/${selected?.id}/site`
      : isTank
        ? `storage-locations/${selected?.id}/tank`
        : isShelf
          ? `storage-locations/${selected?.id}/shelf`
          : kind === 'location'
            ? 'storage-locations'
            : 'storage-boxes';
    const body =
      kind === 'box'
        ? values.specMode === 'custom'
          ? {
              ...values,
              specId: undefined,
              specMode: undefined,
            }
          : {
              parentId: values.parentId,
              code: values.code,
              nameZh: values.nameZh,
              nameEn: values.nameEn,
              specId: values.specId,
            }
        : kind === 'location' && values.type === 'SITE'
          ? {
              type: values.type,
              nameZh: values.nameZh,
              nameEn: values.nameEn,
            }
          : values;
    const response = await fetch(`/api/v1/${path}`, {
      method: isSite || isTank || isShelf ? 'PUT' : 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      await messageApi.error(
        response.status === 409 && (isTank || isShelf)
          ? t('storage.structureReduceBlocked')
          : t('storage.saveFailed'),
      );
      return;
    }
    setKind(undefined);
    await load();
    await messageApi.success(t('storage.saved'));
  };

  const confirmDeleteSelected = () => {
    if (
      !selected ||
      !['SITE', 'DEVICE', 'SHELF', 'BOX'].includes(selected.type)
    )
      return;
    Modal.confirm({
      title: t('storage.deleteLocation'),
      content: t('storage.deleteLocationConfirm'),
      okText: t('storage.confirmDelete'),
      cancelText: t('common.cancel'),
      okButtonProps: { danger: true },
      onOk: () => deleteSelected(),
    });
  };

  const confirmDisableSelected = () => {
    if (!selected || !['SHELF', 'BOX'].includes(selected.type)) return;
    Modal.confirm({
      title: t('storage.disableLocation'),
      content: t('storage.disableLocationConfirm'),
      okText: t('storage.disable'),
      cancelText: t('common.cancel'),
      okButtonProps: { danger: true },
      onOk: () => updateSelectedStatus('INACTIVE'),
    });
  };

  const deleteSelected = async () => {
    if (
      !selected ||
      !['SITE', 'DEVICE', 'SHELF', 'BOX'].includes(selected.type)
    )
      return;
    const fallbackId = selected.parentId ?? undefined;
    const response = await fetch(`/api/v1/storage-locations/${selected.id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      await messageApi.error(
        response.status === 409
          ? t('storage.deleteBlocked')
          : t('storage.deleteFailed'),
      );
      return;
    }
    setBox(undefined);
    setSelectedPosition(undefined);
    setSelectedId(fallbackId);
    await load();
    await messageApi.success(t('storage.deleted'));
  };

  const updateSelectedStatus = async (status: 'ACTIVE' | 'INACTIVE') => {
    if (!selected || !['SHELF', 'BOX'].includes(selected.type)) return;
    const response = await fetch(
      `/api/v1/storage-locations/${selected.id}/status`,
      {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ status, version: selected.version }),
      },
    );
    if (!response.ok) {
      await messageApi.error(
        response.status === 409
          ? t('storage.disableBlocked')
          : t('storage.saveFailed'),
      );
      return;
    }
    await load();
    await messageApi.success(t('storage.saved'));
  };

  const selectedBox = selected?.type === 'BOX' && box ? box : undefined;
  const detailTitle = selected ? (
    selectedBox ? (
      <Space size={6} wrap>
        <Button
          type="text"
          shape="circle"
          size="small"
          icon={<LeftOutlined />}
          disabled={!previousBox}
          aria-label={t('storage.previousBox')}
          title={t('storage.previousBox')}
          onClick={() => openBoxById(previousBox?.id)}
        />
        <span className="storage-detail-title">
          {title(selectedBox) || selectedBox.code}
        </span>
        <Button
          type="text"
          shape="circle"
          size="small"
          icon={<RightOutlined />}
          disabled={!nextBox}
          aria-label={t('storage.nextBox')}
          title={t('storage.nextBox')}
          onClick={() => openBoxById(nextBox?.id)}
        />
        {boxSiblings.length > 1 && (
          <span className="storage-box-position">
            {t('storage.boxNavigationIndex', {
              index: boxSiblingIndex + 1,
              total: boxSiblings.length,
            })}
          </span>
        )}
      </Space>
    ) : (
      <Space>
        <span className="storage-detail-title">
          {selected.id === ROOT_ID
            ? t('storage.rootName')
            : selectedLocationLabel(selected)}
        </span>
      </Space>
    )
  ) : undefined;
  const detailExtra = selected ? (
    selectedBox ? (
      <Dropdown
        trigger={['click']}
        open={boxActionsOpen}
        onOpenChange={setBoxActionsOpen}
        menu={{
          items: [
            selected.status === 'ACTIVE'
              ? {
                  key: 'disable',
                  icon: <StopOutlined />,
                  label: t('storage.disable'),
                  disabled: selected.hasSamplesInSubtree,
                }
              : {
                  key: 'enable',
                  icon: <CheckCircleOutlined />,
                  label: t('storage.enable'),
                },
            {
              key: 'delete',
              danger: true,
              icon: <DeleteOutlined />,
              label: t('storage.delete'),
              disabled: selected.hasSamplesInSubtree,
            },
          ],
          onClick: ({ key }) => {
            if (key === 'delete') {
              if (selected.hasSamplesInSubtree) return;
              confirmDeleteSelected();
            }
            if (key === 'disable') confirmDisableSelected();
            if (key === 'enable') void updateSelectedStatus('ACTIVE');
          },
        }}
      >
        <Button
          type="text"
          icon={boxActionsOpen ? <UpOutlined /> : <DownOutlined />}
        >
          {t('storage.actions')}
        </Button>
      </Dropdown>
    ) : selected.id === ROOT_ID ? (
      <Space>
        <Button icon={<PlusOutlined />} onClick={() => open('location')}>
          {t('storage.createSite')}
        </Button>
      </Space>
    ) : selected.type === 'SITE' ? (
      <Space>
        <Button icon={<PlusOutlined />} onClick={() => open('location')}>
          {t('storage.createDevice')}
        </Button>
        <Dropdown
          trigger={['click']}
          open={locationActionsOpen}
          onOpenChange={setLocationActionsOpen}
          menu={{
            items: [
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: t('storage.edit'),
              },
              {
                key: 'delete',
                danger: true,
                icon: <DeleteOutlined />,
                label: t('storage.delete'),
                disabled: selected.hasSamplesInSubtree,
              },
            ],
            onClick: ({ key }) => {
              if (key === 'edit') editSite();
              if (key === 'delete') {
                if (selected.hasSamplesInSubtree) return;
                confirmDeleteSelected();
              }
            },
          }}
        >
          <Button
            type="text"
            icon={locationActionsOpen ? <UpOutlined /> : <DownOutlined />}
          >
            {t('storage.actions')}
          </Button>
        </Dropdown>
      </Space>
    ) : selected.type === 'DEVICE' ? (
      <Dropdown
        trigger={['click']}
        open={locationActionsOpen}
        onOpenChange={setLocationActionsOpen}
        menu={{
          items: [
            {
              key: 'edit',
              icon: <EditOutlined />,
              label: t('storage.edit'),
            },
            {
              key: 'delete',
              danger: true,
              icon: <DeleteOutlined />,
              label: t('storage.delete'),
              disabled: selected.hasSamplesInSubtree,
            },
          ],
          onClick: ({ key }) => {
            if (key === 'edit') editTank();
            if (key === 'delete') {
              if (selected.hasSamplesInSubtree) return;
              confirmDeleteSelected();
            }
          },
        }}
      >
        <Button
          type="text"
          icon={locationActionsOpen ? <UpOutlined /> : <DownOutlined />}
        >
          {t('storage.actions')}
        </Button>
      </Dropdown>
    ) : selected.type === 'SHELF' ? (
      <Space>
        {!selected.capacityLocked && (
          <Button icon={<PlusOutlined />} onClick={() => open('box')}>
            {t('storage.createBox')}
          </Button>
        )}
        <Dropdown
          trigger={['click']}
          open={locationActionsOpen}
          onOpenChange={setLocationActionsOpen}
          menu={{
            items: [
              {
                key: 'edit',
                icon: <EditOutlined />,
                label: t('storage.edit'),
              },
              selected.status === 'ACTIVE'
                ? {
                    key: 'disable',
                    icon: <StopOutlined />,
                    label: t('storage.disable'),
                    disabled: selected.hasSamplesInSubtree,
                  }
                : {
                    key: 'enable',
                    icon: <CheckCircleOutlined />,
                    label: t('storage.enable'),
                  },
              ...(selected.capacityLocked
                ? []
                : [
                    {
                      key: 'delete',
                      danger: true,
                      icon: <DeleteOutlined />,
                      label: t('storage.delete'),
                      disabled: selected.hasSamplesInSubtree,
                    },
                  ]),
            ],
            onClick: ({ key }) => {
              if (key === 'edit') editShelf();
              if (key === 'disable') confirmDisableSelected();
              if (key === 'enable') void updateSelectedStatus('ACTIVE');
              if (key === 'delete') {
                if (selected.hasSamplesInSubtree) return;
                confirmDeleteSelected();
              }
            },
          }}
        >
          <Button
            type="text"
            icon={locationActionsOpen ? <UpOutlined /> : <DownOutlined />}
          >
            {t('storage.actions')}
          </Button>
        </Dropdown>
      </Space>
    ) : undefined
  ) : undefined;

  const selectedCapacityTotal = selected
    ? selected.id === ROOT_ID
      ? children.reduce((sum, item) => sum + (item.capacityTotal ?? 0), 0)
      : (selected.capacityTotal ?? 0)
    : 0;
  const selectedCapacityOccupied = selected
    ? selected.id === ROOT_ID
      ? children.reduce((sum, item) => sum + (item.capacityOccupied ?? 0), 0)
      : (selected.capacityOccupied ?? 0)
    : 0;
  const selectedCapacityPercent =
    selectedCapacityTotal > 0
      ? Math.round((selectedCapacityOccupied / selectedCapacityTotal) * 100)
      : 0;
  const renderCapacity = (row: Location) =>
    row.capacityTotal > 0 ? (
      <Space size={6}>
        <Progress
          percent={Math.round((row.capacityOccupied / row.capacityTotal) * 100)}
          size="small"
          strokeWidth={6}
          style={{ width: 96 }}
        />
        <span style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.65)' }}>
          {row.capacityOccupied}/{row.capacityTotal}
        </span>
      </Space>
    ) : (
      <span style={{ color: 'rgba(0, 0, 0, 0.45)' }}>-</span>
    );
  const renderStatus = (row: Location) => (
    <Tag color={row.status === 'ACTIVE' ? 'green' : 'orange'}>{row.status}</Tag>
  );
  const positionLabel = (position: Position) =>
    boxLabelMode === 'numeric'
      ? String(
          box?.positionOrder === 'COLUMN_MAJOR'
            ? (position.columnIndex - 1) * (box?.rows ?? 1) + position.rowIndex
            : (position.rowIndex - 1) * (box?.columns ?? 1) +
                position.columnIndex,
        )
      : position.label;
  const changeBoxLabelMode = (mode: 'alphanumeric' | 'numeric') => {
    setBoxLabelMode(mode);
    window.localStorage.setItem('storage.boxLabelMode', mode);
  };
  const positionDisplayText = (position: Position) => {
    if (!position.sample) return '';
    switch (positionDisplay) {
      case 'none':
        return '';
      case 'cellLineMaterial':
        return [position.sample.cellLineCode, position.sample.materialName]
          .filter(Boolean)
          .join(' · ');
      case 'label':
        return sampleLabel(position.sample) ?? '';
      case 'passage':
        return position.sample.passage ?? '';
      default:
        return position.sample.sampleNo;
    }
  };
  const changePositionDisplay = (
    mode: 'none' | 'sampleNo' | 'cellLineMaterial' | 'label' | 'passage',
  ) => {
    setPositionDisplay(mode);
    window.localStorage.setItem('storage.positionDisplay', mode);
  };
  const changeBoxViewMode = (mode: 'grid' | 'list') => {
    setBoxViewMode(mode);
    window.localStorage.setItem('storage.boxViewMode', mode);
  };
  const openBoxById = (id?: string) => {
    if (!id) return;
    setSelectedPosition(undefined);
    setSelectedPositionIds([]);
    setBoxActionsOpen(false);
    setLocationActionsOpen(false);
    setSelectedId(id);
  };
  const renderSampleStatus = (status: string) => (
    <Tag
      color={
        status === 'AVAILABLE'
          ? 'green'
          : status === 'QUARANTINED'
            ? 'orange'
            : status === 'MISSING'
              ? 'red'
              : 'blue'
      }
    >
      {t(`samples.statusOptions.${status}`, { defaultValue: status })}
    </Tag>
  );
  const displaySampleDate = (value?: string | null) => {
    const date = formatTenantDate(value);
    return date === '-' ? '-' : date;
  };

  return (
    <section>
      {holder}
      <ProCard
        variant="outlined"
        headerBordered
        title={t('storage.management')}
      >
        <Row>
          <Col xs={24} lg={6} xl={5} xxl={4} className="storage-browser-col">
            <div className="storage-pane-header">
              <span>{t('storage.browser')}</span>
            </div>
            {treeData.length ? (
              <Tree
                className="storage-tree"
                expandedKeys={expandedKeys}
                selectedKeys={selectedId ? [selectedId] : []}
                showLine
                treeData={treeData}
                onExpand={(keys) => setExpandedKeys(keys.map(String))}
                onSelect={(_, info) => {
                  const key = String(info.node.key);
                  setSelectedId(key);
                }}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Col>
          <Col
            xs={24}
            lg={18}
            xl={19}
            xxl={20}
            className={
              selected?.type === 'BOX' && box
                ? 'storage-detail-col storage-box-card'
                : 'storage-detail-col'
            }
          >
            {selected && (
              <div className="storage-pane-header">
                {detailTitle}
                {detailExtra}
              </div>
            )}
            {selected ? (
              selected.type === 'BOX' && box ? (
                <>
                  <Typography.Paragraph type="secondary">
                    {displayPath(selected.path)}
                  </Typography.Paragraph>
                  <div className="storage-box-meta">
                    <span className="storage-box-meta-item">
                      <span className="storage-box-meta-label">
                        {t('storage.boxSpec')}
                      </span>
                      {boxSpecLabel(selected)}
                    </span>
                    <span className="storage-box-meta-divider" />
                    <span className="storage-box-meta-item">
                      <span className="storage-box-meta-label">
                        {t('storage.capacity')}
                      </span>
                      <Progress
                        percent={
                          box.positions.length
                            ? Math.round(
                                (occupiedCount / box.positions.length) * 100,
                              )
                            : 0
                        }
                        size="small"
                        strokeWidth={6}
                        showInfo={false}
                        style={{ width: 72 }}
                      />
                      <span>
                        {occupiedCount}/{box.positions.length}
                      </span>
                    </span>
                    <span className="storage-box-meta-divider" />
                    <span className="storage-box-meta-item">
                      <span className="storage-box-meta-label">
                        {t('storage.available')}
                      </span>
                      {box.positions.length - occupiedCount}
                    </span>
                  </div>
                  {boxLoadFailed && (
                    <Alert
                      type="error"
                      showIcon
                      title={t('storage.loadFailed')}
                      action={
                        <Button onClick={() => void inspect(selected.id)}>
                          {t('storage.workspace.retry')}
                        </Button>
                      }
                    />
                  )}
                  <Spin spinning={boxLoading}>
                    <div
                      className="storage-box-transition"
                      aria-busy={boxLoading}
                      inert={boxLoading || box.id !== selected.id}
                    >
                      <StorageBoxWorkspace
                        key={box.id}
                        box={box}
                        selected={selectedPosition}
                        selectedIds={selectedPositionIds}
                        onSelectedIds={setSelectedPositionIds}
                        onSelect={setSelectedPosition}
                        view={boxViewMode}
                        onView={changeBoxViewMode}
                        labelMode={boxLabelMode}
                        onLabelMode={changeBoxLabelMode}
                        display={positionDisplay}
                        onDisplay={changePositionDisplay}
                        positionLabel={positionLabel}
                        positionText={positionDisplayText}
                        sampleLabel={sampleLabel}
                        ownerName={ownerName}
                        sampleNotes={sampleNotes}
                        sampleStatus={renderSampleStatus}
                        date={displaySampleDate}
                        canCreate={Boolean(
                          currentUser?.permissions.includes('sample:create') &&
                            (canExecuteInventory ||
                              currentUser?.permissions.includes(
                                'inventory:request',
                              )),
                        )}
                        canRequest={Boolean(
                          currentUser?.permissions.includes(
                            'inventory:request',
                          ),
                        )}
                        canRemove={canExecuteInventory}
                        canEdit={Boolean(
                          currentUser?.permissions.includes('sample:update'),
                        )}
                        onCreate={(positions) => {
                          void openBatchPutaway(positions);
                        }}
                        onEdit={(position) => {
                          void openEditSample(position);
                        }}
                        onRemove={(position) => {
                          void openRemoveSample(position);
                        }}
                        onRequest={(positions) => {
                          void createRequestForSelectedSamples(positions);
                        }}
                        onBatchRemove={() => {
                          void openBatchRemoveSamples();
                        }}
                      />
                    </div>
                  </Spin>
                </>
              ) : (
                <>
                  <div className="storage-info-cards">
                    <StorageInfoCard label={t('storage.code')}>
                      {selected.code}
                    </StorageInfoCard>
                    <StorageInfoCard label={t('storage.type')}>
                      {typeLabel(selected.type)}
                    </StorageInfoCard>
                    {(selected.type === 'DEVICE' ||
                      selected.type === 'SHELF') && (
                      <StorageInfoCard label={t('storage.manager')}>
                        {managerLabel(selected)}
                      </StorageInfoCard>
                    )}
                    <StorageInfoCard label={t('storage.capacity')}>
                      <Space size={6}>
                        <Progress
                          percent={selectedCapacityPercent}
                          size="small"
                          strokeWidth={6}
                          style={{ width: 56 }}
                        />
                        <span>
                          {selectedCapacityOccupied}/{selectedCapacityTotal}
                        </span>
                      </Space>
                    </StorageInfoCard>
                  </div>
                  <Typography.Title level={5}>
                    {t('storage.children')}
                  </Typography.Title>
                  <Table
                    size="small"
                    rowKey="id"
                    childrenColumnName="nestedChildren"
                    dataSource={children}
                    tableLayout="fixed"
                    scroll={{ x: 'max-content' }}
                    pagination={false}
                    locale={{ emptyText: t('overview.empty') }}
                    onRow={(row) => ({
                      onClick: () => setSelectedId(row.id),
                      style: { cursor: 'pointer' },
                    })}
                    columns={
                      selected.type === 'SHELF'
                        ? [
                            {
                              title: t('storage.name'),
                              render: (_, row: Location) => title(row),
                            },
                            {
                              title: t('storage.boxSpec'),
                              render: (_, row: Location) => boxSpecLabel(row),
                            },
                            {
                              title: t('storage.status'),
                              render: (_, row: Location) => renderStatus(row),
                            },
                            {
                              title: t('storage.capacity'),
                              render: (_, row: Location) => renderCapacity(row),
                            },
                          ]
                        : [
                            {
                              title: t('storage.name'),
                              render: (_, row: Location) => title(row),
                            },
                            {
                              title: t('storage.type'),
                              render: (_, row: Location) => typeLabel(row.type),
                            },
                            {
                              title: t('storage.status'),
                              render: (_, row: Location) => renderStatus(row),
                            },
                            {
                              title: t('storage.capacity'),
                              render: (_, row: Location) => renderCapacity(row),
                            },
                          ]
                    }
                  />
                </>
              )
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Col>
        </Row>
      </ProCard>
      <Modal
        open={!!kind}
        title={
          kind === 'tank'
            ? t('storage.editTank')
            : kind === 'site'
              ? t('storage.editSite')
              : kind === 'shelf'
                ? t('storage.editShelf')
                : kind === 'location'
                  ? watchedLocationType === 'DEVICE'
                    ? t('storage.createDevice')
                    : t('storage.createSite')
                  : t('storage.createBox')
        }
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        onCancel={() => setKind(undefined)}
        onOk={() => void form.submit()}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => void save(values)}
        >
          {kind === 'location' && (
            <>
              <Form.Item
                name="type"
                label={t('storage.type')}
                initialValue="SITE"
                rules={[{ required: true }]}
              >
                <Select
                  disabled
                  options={['SITE', 'DEVICE'].map((value) => ({
                    value,
                    label: typeLabel(value),
                  }))}
                />
              </Form.Item>
              {watchedLocationType === 'DEVICE' && (
                <Form.Item
                  name="parentId"
                  label={t('storage.parent')}
                  rules={[{ required: true }]}
                >
                  <Select
                    disabled
                    options={locations
                      .filter((item) => item.type === 'SITE')
                      .map((item) => ({
                        value: item.id,
                        label: `${displayPath(item.path)} · ${typeLabel(item.type)}`,
                      }))}
                  />
                </Form.Item>
              )}
              {watchedLocationType === 'DEVICE' && (
                <>
                  <Form.Item
                    name="deviceSpecId"
                    label={t('storage.deviceSpec')}
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={deviceSpecs.map((item) => ({
                        value: item.id,
                        label: deviceSpecLabel(item),
                      }))}
                    />
                  </Form.Item>
                  <Form.Item
                    name="defaultBoxSpecId"
                    label={t('storage.defaultBoxSpec')}
                    initialValue={globalDefaultSpec?.id}
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={specs.map((item) => ({
                        value: item.id,
                        label: specLabel(item),
                      }))}
                    />
                  </Form.Item>
                  <Form.Item
                    name="canisterNamingRule"
                    label={t('storage.canisterNamingRule')}
                    tooltip={t('storage.canisterNamingRuleHint')}
                    initialValue="{A}冻存架"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="boxNamingRule"
                    label={t('storage.boxNamingRule')}
                    tooltip={t('storage.boxNamingRuleHint')}
                    initialValue="BOX-{n}"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item name="managerUserId" label={t('storage.manager')}>
                    <Select allowClear options={managerOptions} />
                  </Form.Item>
                  <Form.Item
                    name="description"
                    label={t('storage.description')}
                  >
                    <Input.TextArea rows={3} />
                  </Form.Item>
                </>
              )}
            </>
          )}
          {kind === 'box' && (
            <>
              <Form.Item
                name="parentId"
                label={t('storage.parent')}
                rules={[{ required: true }]}
              >
                <Select
                  disabled
                  options={locations
                    .filter((item) => item.type === 'SHELF')
                    .map((item) => ({
                      value: item.id,
                      label: `${displayPath(item.path)} · ${typeLabel(item.type)}`,
                    }))}
                />
              </Form.Item>
              <Form.Item
                name="specMode"
                label={t('storage.specificationMode')}
                initialValue="preset"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { value: 'preset', label: t('storage.usePresetSpec') },
                    { value: 'custom', label: t('storage.useCustomSpec') },
                  ]}
                />
              </Form.Item>
              {watchedSpecMode !== 'custom' && (
                <>
                  <Form.Item
                    name="specId"
                    label={t('storage.specification')}
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={specs.map((item) => ({
                        value: item.id,
                        label: specLabel(item),
                      }))}
                    />
                  </Form.Item>
                  {inheritedBoxSpec && (
                    <Alert
                      showIcon
                      type="info"
                      style={{ marginBottom: 16 }}
                      message={t('storage.inheritedBoxSpec', {
                        spec: specLabel(inheritedBoxSpec),
                      })}
                    />
                  )}
                </>
              )}
            </>
          )}
          {(kind === 'box' || kind === 'site') && (
            <Form.Item
              name="code"
              label={t('storage.code')}
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          )}
          <Form.Item
            name="nameZh"
            label={t('storage.nameZh')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="nameEn" label={t('storage.nameEn')}>
            <Input />
          </Form.Item>
          {kind === 'site' && (
            <Form.Item name="version" hidden>
              <Input />
            </Form.Item>
          )}
          {(kind === 'tank' ||
            (kind === 'shelf' && !selected?.capacityLocked)) && (
            <Form.Item
              name="defaultBoxSpecId"
              label={t('storage.defaultBoxSpec')}
            >
              <Select
                allowClear
                placeholder={
                  kind === 'shelf'
                    ? t('storage.inheritValue', {
                        value: specLabel(effectiveShelfBoxSpec),
                      })
                    : undefined
                }
                options={specs.map((item) => ({
                  value: item.id,
                  label: specLabel(item),
                }))}
              />
            </Form.Item>
          )}
          {kind === 'tank' && (
            <>
              <Form.Item name="managerUserId" label={t('storage.manager')}>
                <Select allowClear options={managerOptions} />
              </Form.Item>
              {selected?.capacityLocked ? (
                <>
                  <Alert
                    showIcon
                    type="info"
                    style={{ marginBottom: 16 }}
                    message={t('storage.capacityLockedHint')}
                  />
                  <Form.Item
                    name="canisterNamingRule"
                    label={t('storage.canisterNamingRule')}
                    tooltip={t('storage.canisterNamingRuleHint')}
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="RACK-{A}" />
                  </Form.Item>
                  <Form.Item
                    name="boxNamingRule"
                    label={t('storage.boxNamingRule')}
                    tooltip={t('storage.boxNamingRuleHint')}
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="BOX-{n}" />
                  </Form.Item>
                </>
              ) : (
                <>
                  <Form.Item
                    name="canisterCount"
                    label={t('storage.canisterCount')}
                  >
                    <InputNumber
                      min={0}
                      max={200}
                      precision={0}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  {isReducingCanisters && (
                    <Alert
                      showIcon
                      type="warning"
                      style={{ marginBottom: 16 }}
                      message={t('storage.canisterReduceWarning', {
                        current: selectedCanisterCount,
                        next: nextCanisterCount,
                      })}
                    />
                  )}
                  <Form.Item
                    name="canisterNamingRule"
                    label={t('storage.canisterNamingRule')}
                    tooltip={t('storage.canisterNamingRuleHint')}
                  >
                    <Input placeholder="RACK-{A}" />
                  </Form.Item>
                  <Form.Item name="boxCount" label={t('storage.boxCount')}>
                    <InputNumber
                      min={0}
                      max={500}
                      precision={0}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  {isReducingTankBoxes && (
                    <Alert
                      showIcon
                      type="warning"
                      style={{ marginBottom: 16 }}
                      message={t('storage.boxReduceWarning', {
                        current: maxTankCanisterBoxCount,
                        next: nextBoxCount,
                      })}
                    />
                  )}
                  <Form.Item
                    name="boxNamingRule"
                    label={t('storage.boxNamingRule')}
                    tooltip={t('storage.boxNamingRuleHint')}
                  >
                    <Input placeholder="BOX-{n}" />
                  </Form.Item>
                </>
              )}
              <Form.Item name="description" label={t('storage.description')}>
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item name="version" hidden>
                <Input />
              </Form.Item>
            </>
          )}
          {kind === 'box' && (
            <>
              {watchedSpecMode === 'custom' && (
                <>
                  <Form.Item
                    name="rows"
                    label={t('storage.rows')}
                    rules={[{ required: true }]}
                  >
                    <InputNumber min={1} max={52} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    name="columns"
                    label={t('storage.columns')}
                    rules={[{ required: true }]}
                  >
                    <InputNumber min={1} max={200} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    name="rowLabelRule"
                    label={t('storage.rowLabel')}
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={['ALPHA', 'NUMERIC'].map((value) => ({
                        value,
                        label: value,
                      }))}
                    />
                  </Form.Item>
                  <Form.Item
                    name="columnLabelRule"
                    label={t('storage.columnLabel')}
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={['ALPHA', 'NUMERIC'].map((value) => ({
                        value,
                        label: value,
                      }))}
                    />
                  </Form.Item>
                  <Form.Item
                    name="positionOrder"
                    label={t('storage.positionOrder')}
                    rules={[{ required: true }]}
                  >
                    <Select
                      options={['ROW_MAJOR', 'COLUMN_MAJOR'].map((value) => ({
                        value,
                        label: value,
                      }))}
                    />
                  </Form.Item>
                </>
              )}
            </>
          )}
          {kind === 'shelf' && (
            <>
              <Form.Item name="managerUserId" label={t('storage.manager')}>
                <Select allowClear options={managerOptions} />
              </Form.Item>
              {selected?.capacityLocked ? (
                <Alert
                  showIcon
                  type="info"
                  style={{ marginBottom: 16 }}
                  message={t('storage.capacityLockedHint')}
                />
              ) : (
                <>
                  <Form.Item name="boxCount" label={t('storage.boxCount')}>
                    <InputNumber
                      min={0}
                      max={500}
                      precision={0}
                      placeholder={t('storage.inheritValue', {
                        value: inheritedShelfBoxCount ?? '-',
                      })}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  {isReducingBoxes && (
                    <Alert
                      showIcon
                      type="warning"
                      style={{ marginBottom: 16 }}
                      message={t('storage.boxReduceWarning', {
                        current: selectedBoxCount,
                        next: nextBoxCount,
                      })}
                    />
                  )}
                  <Form.Item
                    name="boxNamingRule"
                    label={t('storage.boxNamingRule')}
                    tooltip={t('storage.boxNamingRuleHint')}
                  >
                    <Input
                      placeholder={t('storage.inheritValue', {
                        value: inheritedShelfBoxRule ?? '-',
                      })}
                    />
                  </Form.Item>
                </>
              )}
              <Form.Item name="description" label={t('storage.description')}>
                <Input.TextArea rows={3} />
              </Form.Item>
              <Form.Item name="version" hidden>
                <Input />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
      <Modal
        open={sampleOpen}
        title={
          editingSample ? t('samples.editTitle') : t('samples.createTitle')
        }
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        width={720}
        confirmLoading={cellLinesLoading}
        onCancel={() => {
          setSampleOpen(false);
          setEditingSample(undefined);
        }}
        onOk={() => void sampleForm.submit()}
      >
        <Form
          form={sampleForm}
          layout="vertical"
          onFinish={(values) => void saveSample(values)}
        >
          <Row gutter={16}>
            {editingSample && (
              <Col xs={24} md={12}>
                <Form.Item name="sampleNo" label={t('samples.number')}>
                  <Input readOnly />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={12}>
              <Form.Item name="label" label={t('samples.label')}>
                <Input placeholder={t('samples.labelPlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="barcode" label={t('samples.barcode')}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="cellLineId"
                label={
                  <Space>
                    <span>{t('samples.cellLine')}</span>
                    <Button
                      size="small"
                      type="link"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        cellLineForm.resetFields();
                        cellLineForm.setFieldsValue({
                          originType: 'COMMERCIAL',
                        });
                        setCellLineOpen(true);
                      }}
                    >
                      {t('cellLines.create')}
                    </Button>
                  </Space>
                }
                rules={[{ required: true }]}
              >
                <Select
                  showSearch
                  loading={cellLinesLoading}
                  optionFilterProp="label"
                  onChange={(cellLineId: string) => {
                    sampleForm.setFieldValue(
                      'cellMaterialId',
                      defaultMaterialId(cellLineId),
                    );
                  }}
                  options={cellLineOptions}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="cellMaterialId"
                label={
                  <Space>
                    <span>{t('samples.material')}</span>
                    <Button
                      size="small"
                      type="link"
                      icon={<PlusOutlined />}
                      onClick={() => openCreateMaterial()}
                    >
                      {t('samples.createMaterial')}
                    </Button>
                  </Space>
                }
              >
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  options={materialsForLine(selectedSampleCellLineId).map(
                    (material) => ({
                      value: material.id,
                      label: materialLabel(material),
                    }),
                  )}
                  placeholder={t('samples.parentalMaterial')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="passage" label={t('samples.passage')}>
                <Input placeholder="P12" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="ownerName"
                label={t('samples.ownerName')}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t('storage.entry.validation.required'),
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="notes" label={t('samples.notes')}>
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="cellCount"
                label={t('samples.cellCount')}
                tooltip={t('samples.cellCountHint')}
              >
                <Input placeholder="1e6" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="storagePositionId"
                label={t('samples.position')}
                rules={[{ required: true }]}
              >
                <Select
                  disabled
                  options={
                    selectedPosition
                      ? [
                          {
                            value: selectedPosition.id,
                            label: `${displayPath(selected?.path)}/${positionLabel(selectedPosition)}`,
                          },
                        ]
                      : []
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="freezeDate"
                label={t('samples.freezeDate')}
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="expiryDate" label={t('samples.expiryDate')}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Modal
        open={cellLineOpen}
        title={t('cellLines.createTitle')}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        onCancel={() => setCellLineOpen(false)}
        onOk={() => void cellLineForm.submit()}
      >
        <Form
          form={cellLineForm}
          layout="vertical"
          onFinish={(values) => void saveCellLine(values)}
        >
          <Form.Item
            name="code"
            label={t('cellLines.code')}
            rules={[{ required: true }]}
          >
            <Input placeholder={t('cellLines.codePlaceholder')} />
          </Form.Item>
          <Form.Item
            name="species"
            label={t('cellLines.species')}
            rules={[{ required: true }]}
          >
            <AutoComplete
              options={speciesOptions}
              placeholder={t('cellLines.speciesPlaceholder')}
              filterOption={(inputValue, option) =>
                String(option?.value ?? '')
                  .toLowerCase()
                  .includes(inputValue.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item
            name="originType"
            label={t('cellLines.originType')}
            initialValue="COMMERCIAL"
            rules={[{ required: true }]}
          >
            <Select options={originTypeOptions} />
          </Form.Item>
          <Form.Item name="source" label={t('cellLines.source')}>
            <Input />
          </Form.Item>
          <Form.Item name="catalogNo" label={t('cellLines.catalogNo')}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label={t('cellLines.description')}>
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={materialOpen}
        title={t('samples.createMaterial')}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        onCancel={() => setMaterialOpen(false)}
        onOk={() => void materialForm.submit()}
        width={720}
      >
        <Form
          form={materialForm}
          layout="vertical"
          onFinish={(values) => void saveMaterial(values)}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="cellLineId"
                label={t('samples.cellLine')}
                rules={[{ required: true }]}
              >
                <Select disabled options={cellLineOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="type"
                label={helpLabel(
                  t('samples.materialType'),
                  t('samples.materialTooltips.materialType'),
                )}
                rules={[{ required: true }]}
              >
                <Select options={materialTypeOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label={helpLabel(
                  t('samples.materialName'),
                  t('samples.materialTooltips.materialName'),
                )}
                rules={[{ required: true }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="parentMaterialId"
                label={helpLabel(
                  t('samples.parentMaterial'),
                  t('samples.materialTooltips.parentMaterial'),
                )}
              >
                <Select
                  allowClear
                  options={materialsForLine(
                    materialForm.getFieldValue('cellLineId') as
                      | string
                      | undefined,
                  ).map((material) => ({
                    value: material.id,
                    label: materialLabel(material),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="modificationType"
                label={t('samples.modificationType')}
              >
                <Select allowClear options={modificationTypeOptions} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="targetGene"
                label={helpLabel(
                  t('samples.targetGene'),
                  t('samples.materialTooltips.targetGene'),
                )}
              >
                <Input />
              </Form.Item>
            </Col>
            {selectedMaterialModificationType === 'TAG' ? (
              <>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="tagName"
                    label={helpLabel(
                      t('samples.tagName'),
                      t('samples.materialTooltips.tagName'),
                    )}
                    preserve={false}
                  >
                    <Input placeholder="HiBiT" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="tagPosition"
                    label={helpLabel(
                      t('samples.tagPosition'),
                      t('samples.materialTooltips.tagPosition'),
                    )}
                    preserve={false}
                  >
                    <Select allowClear options={tagPositionOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="modificationStrategy"
                    label={helpLabel(
                      t('samples.modificationStrategy'),
                      t('samples.materialTooltips.modificationStrategy'),
                    )}
                    preserve={false}
                  >
                    <Select allowClear options={modificationStrategyOptions} />
                  </Form.Item>
                </Col>
              </>
            ) : null}
            <Col xs={24} md={12}>
              <Form.Item
                name="constructName"
                label={helpLabel(
                  t('samples.constructName'),
                  t('samples.materialTooltips.constructName'),
                )}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="selectionMarker"
                label={helpLabel(
                  t('samples.selectionMarker'),
                  t('samples.materialTooltips.selectionMarker'),
                )}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="cloneNo"
                label={helpLabel(
                  t('samples.cloneNo'),
                  t('samples.materialTooltips.cloneNo'),
                )}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="validationStatus"
                label={helpLabel(
                  t('samples.validationStatus'),
                  t('samples.materialTooltips.validationStatus'),
                )}
                rules={[{ required: true }]}
              >
                <Select options={validationStatusOptions} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name="notes" label={t('samples.notes')}>
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      {requestSamples && (
        <RequestsPage
          embeddedSamples={requestSamples}
          onEmbeddedClose={() => setRequestSamples(undefined)}
        />
      )}
      {batchPutawayOpen && box && (
        <CryovialEntryModal
          positions={box.positions.map((position) => ({
            id: position.id,
            label: positionLabel(position),
            occupied: position.occupied,
          }))}
          initialPositionIds={entryPositionIds}
          path={displayPath(selected?.path)}
          cellLines={cellLineOptions}
          materials={materials.map((material) => ({
            id: material.id,
            cellLineId: material.cellLineId,
            type: material.type,
            label: materialLabel(material),
          }))}
          submitting={batchPutawaySubmitting}
          error={entryError}
          canExecute={canExecuteInventory}
          onClose={closeBatchPutaway}
          onCreateCellLine={() => {
            cellLineForm.resetFields();
            cellLineForm.setFieldsValue({ originType: 'COMMERCIAL' });
            setCellLineOpen(true);
          }}
          onCreateMaterial={openCreateMaterial}
          onSubmit={(rows, reason) =>
            saveBatchPutaway({
              reason,
              rows: rows.map((row) => ({
                ...row,
                freezeDate: row.freezeDate ? dayjs(row.freezeDate) : undefined,
                expiryDate: row.expiryDate ? dayjs(row.expiryDate) : undefined,
              })),
            })
          }
          result={
            batchPutawayResult ? (
              <Result
                status="success"
                title={t(
                  batchPutawayResult.kind === 'request'
                    ? 'storage.batchPutawayRequestCreated'
                    : 'storage.batchPutawayExecutionCompleted',
                )}
                subTitle={
                  batchPutawayResult.kind === 'request'
                    ? t('storage.batchPutawayRequestCreatedHint', {
                        requestNo: batchPutawayResult.request.requestNo,
                        count: batchPutawayResult.itemCount,
                      })
                    : t('storage.batchPutawayExecutionCompletedHint', {
                        transactionNo: batchPutawayResult.transactionNo,
                        count: batchPutawayResult.itemCount,
                      })
                }
                extra={
                  batchPutawayResult.kind === 'request' ? (
                    <Button
                      type="primary"
                      onClick={() => {
                        closeBatchPutaway();
                        void navigate('/requests');
                      }}
                    >
                      {t('storage.batchPutawayViewRequest')}
                    </Button>
                  ) : undefined
                }
              />
            ) : null
          }
        />
      )}
      <Modal
        open={!!removingSample}
        title={t('storage.removeSample')}
        okText={t('storage.confirmRemoveSample')}
        cancelText={t('common.cancel')}
        okButtonProps={{ danger: true }}
        onCancel={() => setRemovingSample(undefined)}
        onOk={() => void removeSampleForm.submit()}
      >
        <Alert
          showIcon
          type="warning"
          message={t('storage.removeSampleHint', {
            sample: removingSample?.sampleNo ?? '-',
          })}
          style={{ marginBottom: 16 }}
        />
        <Form
          form={removeSampleForm}
          layout="vertical"
          onFinish={(values) => void removeSample(values)}
        >
          <Form.Item
            name="reason"
            label={t('samples.reason')}
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={batchRemovingSamples.length > 0}
        title={t('storage.batchRemoveSample')}
        okText={t('storage.confirmRemoveSample')}
        cancelText={t('common.cancel')}
        okButtonProps={{ danger: true }}
        onCancel={() => setBatchRemovingSamples([])}
        onOk={() => void batchRemoveForm.submit()}
      >
        <Alert
          showIcon
          type="warning"
          message={t('storage.batchRemoveSampleHint', {
            count: batchRemovingSamples.length,
          })}
          style={{ marginBottom: 16 }}
        />
        <Form
          form={batchRemoveForm}
          layout="vertical"
          onFinish={(values) => void removeSelectedSamples(values)}
        >
          <Form.Item
            name="reason"
            label={t('samples.reason')}
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </section>
  );
}
